import { State } from 'src/replicant/State';
import { Feature } from 'src/replicant/analytics';
import {
  FriendshipRequest,
  FriendShareMethod,
  PlayerID,
  friendListLimit,
} from './friends.ruleset';

/**
 * Get friendship requests list from state as array
 * We only need to get pending requests and requests from others ignore owner requests
 */
export function getFriendshipPendingRequests(
  state: State,
): FriendshipRequest[] {
  const requests = state.friendshipRequests;

  return Object.keys(requests)
    .map((id) => ({
      ...requests[id],
    }))
    .filter((request) => request.status === 'pending')
    .filter((requests) => requests.owner.id !== state.id)
    .sort((a, b) => a.createdAt - b.createdAt) as FriendshipRequest[];
}

/**
 * Get friendship requests sent list from state as array
 * These are the requests that the user has sent to others
 */
export function getFriendshipPendingRequestsSent(
  state: State,
): FriendshipRequest[] {
  const requests = state.friendshipRequests;

  return Object.keys(requests)
    .map((id) => ({
      ...requests[id],
    }))
    .filter((request) => request.status === 'pending')
    .filter((requests) => requests.owner.id === state.id)
    .sort((a, b) => a.createdAt - b.createdAt) as FriendshipRequest[];
}

/**
 * Check if friend request is already sent to target user
 */
export function isFriendshipRequestedSent(state: State, id: PlayerID): boolean {
  const request = state.friendshipRequests[id];

  return !!(request && request.status === 'pending');
}

/**
 * Get badge counter for friendship requests
 */
export function getFriendshipRequestsCount(state: State): number {
  return getFriendshipPendingRequests(state).length;
}

/**
 * Player in friends list, it will check not only playing friends but also NOT playing friends(social friends)
 */
export function isBuruFriend(state: State, id: PlayerID): boolean {
  const friend = state.buruFriends[id];

  // Not a friend if not yet exists
  if (!friend) return false;

  // Not a friend if unfriended
  return friend.unfriendedAt === undefined;
}

/**
 * Check if user and friend were unfriended
 */
export function wasUnfriended(state: State, id: PlayerID): boolean {
  // For unfriends initiated by the user, we can check the friends blacklist
  if (state.friendsBlacklist.includes(id)) return true;

  // Otherwise, we can check if user was unfriended by the friend
  return wasUnfriendedBy(state, id);
}

/**
 * Check if user was unfriended by the friend
 */
export function wasUnfriendedBy(state: State, id: PlayerID): boolean {
  const friend = state.buruFriends[id];

  // Not unfriended if not yet exists
  if (!friend) return false;

  // Is unfriended if unfriendedAt has been set
  return friend.unfriendedAt !== undefined;
}

/**
 * Return saved playing friends list
 */
export function getBuruFriends(state: State): PlayerID[] {
  return (
    Object.entries(state.buruFriends)
      // Filter out unfriended friends
      .filter(([, friend]) => friend.unfriendedAt === undefined)
      .map(([id]) => id)
  );
}

/**
 * Return saved playing friends list, most recently added first.
 *
 * Use this where only a bounded number of friends can be handled — profile
 * fetching is capped at `friendListLimit` — so the ones that survive the cap
 * are the newest friendships rather than an arbitrary slice.
 */
export function getBuruFriendsByRecency(state: State): PlayerID[] {
  return (
    Object.entries(state.buruFriends)
      // Filter out unfriended friends
      .filter(([, friend]) => friend.unfriendedAt === undefined)
      .sort(([, a], [, b]) => b.addedAt - a.addedAt)
      .map(([id]) => id)
  );
}

/**
 * Return the number of active (non-unfriended) buru friends
 */
export function getBuruFriendsCount(state: State): number {
  return getBuruFriends(state).length;
}

/**
 * Return unfriended friends list
 */
export function getUnfriendedFriends(state: State): PlayerID[] {
  return (
    Object.entries(state.buruFriends)
      // Extract friends that unfriended the user
      .filter(([, friend]) => friend.unfriendedAt !== undefined)
      .map(([id]) => id)
  );
}

/**
 * Check if user can send/receive friend requests.
 */
export function canAddFriends(state: State): boolean {
  const friendsCount = getBuruFriendsCount(state);
  const requestsCount = getFriendshipPendingRequests(state).length;

  // Can only send requests if friends count + requests count is less than limit.
  return friendsCount + requestsCount < friendListLimit;
}

/**
 * Get users with pending friend requests from the friendshipRequests state
 */
export function getUsersWithPendingRequest(state: State): string[] {
  return (
    Object.entries(state.friendshipRequests || {})
      // Extract only pending requests
      .filter(([_, { status }]) => status === 'pending')
      .map(([id]) => id)
  );
}

/**
 * Check if a friend can be automatically added from invite.
 * Validates that the friend is not self, not already a friend, and user can add friends.
 */
export function canAutoAddFriend(userState: State, friendId: string): boolean {
  // Skip if friend is self
  if (friendId === userState.id) {
    return false;
  }

  // Skip if already friends
  if (userState.buruFriends[friendId]) {
    return false;
  }

  // Skip if user cannot add friends (friends limit reached)
  if (!canAddFriends(userState)) {
    return false;
  }

  return true;
}

/**
 * Explicit `subFeature → method` mapping for invite / share entry payloads
 * that can lead to `FriendAdd`. Each entry is a known fact; new invite
 * features must add a row here to be attributed. Anything not in the table
 * resolves to `'unknown'` — the analytics-health flag, not a default.
 *
 * `originSubFeature` is no longer remapped: the entry payload's `subFeature`
 * is now the canonical analytics value, so `deriveFriendAddAttribution`
 * passes it through directly.
 */
const SUB_FEATURE_TO_METHOD: Record<string, FriendShareMethod> = {
  // ─── Friends (Add Friends V2) ───────────────────────────────────────────
  [Feature.FRIENDS.LINE_SHARE]: 'line_share',
  [Feature.FRIENDS.NATIVE_SHARE]: 'native_share',
  [Feature.FRIENDS.QRCODE_SHARE]: 'qr_code',

  // ─── Pet Dialog (A/B 0468 V2) ───────────────────────────────────────────
  [Feature.PET_DIALOG.LINE_SHARE]: 'line_share',
  [Feature.PET_DIALOG.NATIVE_SHARE]: 'native_share',
  [Feature.PET_DIALOG.QRCODE_SHARE]: 'qr_code',

  // ─── Friend List popup (resolved A/B 0470) ──────────────────────────────
  [Feature.FRIEND_LIST.LINE_SHARE]: 'line_share',
  [Feature.FRIEND_LIST.NATIVE_SHARE]: 'native_share',

  // ─── Invite (one of the canonical paths to create friends) ──────────────
  [Feature.INVITE.LINE_SHARE]: 'line_share',
  [Feature.INVITE.NATIVE_SHARE]: 'native_share',

  // Cards
  [Feature.CARDS.REQUEST]: 'request',
  [Feature.CARDS.REQUEST_LINE_SHARE]: 'line_share',
  [Feature.CARDS.REQUEST_NATIVE_SHARE]: 'native_share',

  // ─── Buddies ────────────────────────────────────────────────────────────
  [Feature.BUDDIES.LINE_SHARE]: 'line_share',
  [Feature.BUDDIES.NATIVE_SHARE]: 'native_share',

  // ─── Janken ─────────────────────────────────────────────────────────────
  [Feature.JANKEN.LINE_CHALLENGE]: 'line_share',
  [Feature.JANKEN.LINE_REMATCH]: 'line_share',
  [Feature.JANKEN.NATIVE_CHALLENGE]: 'native_share',
  [Feature.JANKEN.NATIVE_REMATCH]: 'native_share',

  // ════════════════════════════════════════════════════════════════════════
  // LEGACY — DO NOT ADD NEW ENTRIES BELOW THIS LINE
  // ════════════════════════════════════════════════════════════════════════
  // In-flight links from older clients still carry these old subFeature
  // strings in their payload. They are kept here only so existing links
  // continue to resolve to the correct method until they age out of
  // circulation. New senders MUST emit the canonical names above instead.
  [Feature.INVITE.INVITE_URL]: 'line_share',
  [Feature.INVITE.INVITE_ADD_FRIENDS_V2]: 'native_share',
  [Feature.FRIENDS.FIND_LINESHARE]: 'line_share',
  [Feature.FRIENDS.FIND_URLSHARE]: 'native_share',
  [Feature.FRIENDS.FIND_QRCODE]: 'qr_code',
  [Feature.BUDDIES.LEGACY_LINE_SHARE]: 'line_share',
  [Feature.BUDDIES.LEGACY_NATIVE_SHARE]: 'native_share',
  [Feature.JANKEN.LEGACY_LINE_CHALLENGE]: 'line_share',
  [Feature.JANKEN.LEGACY_LINE_REMATCH]: 'line_share',
  [Feature.JANKEN.LEGACY_NATIVE_CHALLENGE]: 'native_share',
  [Feature.JANKEN.LEGACY_NATIVE_REMATCH]: 'native_share',
  [Feature.FRIEND_LIST.LEGACY_LINE_SHARE]: 'line_share',
  [Feature.FRIEND_LIST.LEGACY_NATIVE_SHARE]: 'native_share',
  // Boost Rocket was removed in #11759, so these are raw string literals
  // rather than `Feature.*` references — the namespace is gone, but links
  // shared during the 2026-04/05 events still carry these subFeatures.
  boost_rocket_line_share: 'line_share',
  boost_rocket_native_share: 'native_share',
};

/**
 * Resolve the canonical analytics `method` value from an entry payload's
 * `subFeature`. Backed by an explicit hardcoded mapping — no substring
 * matching, no guessing. Anything not in the table returns `'unknown'`.
 */
export function getFriendshipShareMethod(
  subFeature: string | undefined,
): FriendShareMethod {
  if (!subFeature) return 'unknown';
  return SUB_FEATURE_TO_METHOD[subFeature] ?? 'unknown';
}

/**
 * Resolve the canonical analytics `method` value from a stored
 * FriendshipRequest's `method` field. Returns `'unknown'` for missing or
 * unrecognised values — `'unknown'` in dashboards is the analytics-health
 * signal, not a default.
 */
export function getFriendshipRequestMethod(
  method: string | undefined,
): FriendShareMethod {
  switch (method) {
    case 'request':
    case 'line_share':
    case 'native_share':
    case 'qr_code':
      return method;
    default:
      return 'unknown';
  }
}

/**
 * Derive `FriendAdd` / `FriendRequestReceive` attribution from an entry
 * payload's existing `feature` / `subFeature`. The entry's subFeature is now
 * the canonical analytics value, so it's passed through directly; only
 * `method` is resolved via `SUB_FEATURE_TO_METHOD`. Missing or unmapped
 * fields fall back to `'unknown'` — never any other guess. `'unknown'` is
 * reserved as the analytics-health flag.
 */
export function deriveFriendAddAttribution(
  entryData:
    | {
        feature?: string | null;
        subFeature?: string | null;
        $subFeature?: string | null;
      }
    | undefined,
): {
  originFeature: string;
  originSubFeature: string;
  method: FriendShareMethod;
} {
  const subFeature =
    entryData?.subFeature ?? entryData?.$subFeature ?? undefined;
  return {
    originFeature: entryData?.feature ?? 'unknown',
    originSubFeature: subFeature ?? 'unknown',
    method: getFriendshipShareMethod(subFeature),
  };
}
